import { differenceInSeconds } from "date-fns";
import { Dispatch, SetStateAction, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router";
import {
  InlineModal,
  StyledIframe
} from "./ScormWrapper.styles";
import { useNavigate } from "../../services";
import { useAuthStore, useLanguageStore, useNavbarStore } from "../../stores";
import type { LearningActivity, Level } from "../../types";
import { getPreviousRoute } from "../../utils/general";

export function ScormWrapper({
  activityId,
  close,
  courseId,
  // fullScreen=false,
  isOpen,
  learningObjectTypology,
  learningObjectStarted,
  pathId,
  canAccessNow,
  canAccessUntil,
  setTriggerPause
} : {
  activityId: number
  close?: () => void
  courseId: string | null
  // fullScreen?: boolean
  isOpen: boolean
  learningObjectTypology: Level
  learningObjectStarted?: LearningActivity
  pathId: string | null,
  // aggiunti con il refactoring autoplay
  canAccessNow?: boolean,
  canAccessUntil?: string,
  setTriggerPause?:Dispatch<SetStateAction<boolean>>
}) {

  const modalRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Nuovi token letti dalla libreria tracker (aggiornati dal refresh proattivo)
  const tokenRef = useRef<string>("");
  const sessionTokenRef = useRef<string>("");

  const uri = `${window.location.origin}/esplora/scorm/${activityId}/${learningObjectTypology}`;
  const language = useLanguageStore(state => state.language)?.id;

  // il passaggio dei dati tramite questa postMessage mandava status stale E:
  // - Il CTA in dettaglio legge P (giusto).
  // - In watch hai un valore iniziale stale E (cache + refetch in corso).
  // - Quel valore stale viene serializzato e inviato via postMessage.
  // - Nel frame SCORM il dato da postMessage ha priorità e blocca il fetch locale (di fatto non corregge più a P).
  // è stato quindi rimossa la chiamata postMessage per DA_SCORM 2026-05-19
  const shouldUsePostMessageBootstrap = Boolean(learningObjectStarted)
    && learningObjectTypology !== "DA_SCORM";
  const params = `courseId=${courseId || null}&rootId=${pathId || null}&lang=${language}`;
  const navbarItem = useNavbarStore(state => state.item);
  const { pathname }=useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (isOpen) {
      iframeRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (modalRef.current && !document.contains(modalRef.current)) {
        observer.disconnect();
        const previousPath =
          getPreviousRoute({
            courseId: (courseId ? Number(courseId) : undefined),
            id: activityId,
            learningObjectTypology,
            path: pathname,
            rootId: (pathId ? Number(pathId) : undefined),
            section: navbarItem
          });
        navigate(previousPath);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [activityId, learningObjectTypology, courseId, pathId, pathname, navbarItem, navigate]);


  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.data.type === "SCORM_READY") {
        if (
          iframeRef.current
          && iframeRef.current.contentWindow
          && learningObjectStarted
          && shouldUsePostMessageBootstrap
        ) {
          iframeRef.current.contentWindow.postMessage({
            data: learningObjectStarted,
            type: "START_OR_RESUME_DATA"
          }, window.location.origin);
        }
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [learningObjectStarted, shouldUsePostMessageBootstrap]);

  // Refresh reattivo del token: gli hook sono esposti sulla main window e letti
  // dalla libreria via window.top. La libreria chiama olRefreshToken quando una
  // sua chiamata va in 401 per accessToken scaduto (la scadenza varia per
  // corporate, quindi non usiamo un timer proattivo). I token freschi stanno in
  // ref locali, senza toccare lo store (un set() cambierebbe la queryKey di
  // useStartOrResumeQuery e rimonterebbe la lightbox).
  useEffect(() => {
    if (!isOpen) return;

    const storeToken = () =>
      useAuthStore.getState().session?.getAccessToken().getJwtToken() ?? "";
    const storeSession = () =>
      useAuthStore.getState().sessionToken ?? "";

    tokenRef.current = storeToken();
    sessionTokenRef.current = storeSession();

    // Cookie che protegge il contenuto delle slide: va riscritto a ogni refresh.
    const writeContentCookie = (accessToken: string, sessionId: string) => {
      const date = new Date();
      date.setTime(date.getTime() + 24 * 60 * 60 * 1000);
      document.cookie = `digitedUserLO=${JSON.stringify({ accessToken, sessionId })}`
        + `; expires=${date.toUTCString()}; path=/`;
    };

    window.olGetToken = () => tokenRef.current || storeToken();
    window.olGetSessionToken = () => sessionTokenRef.current || storeSession();
    window.olRefreshToken = async () => {
      // Ritorna il nuovo accessToken se il refresh va a buon fine; null se anche
      // il refreshToken e' scaduto -> la libreria mostra il re-login.
      const tokens = await useAuthStore.getState().refreshTokensQuiet();
      if (!tokens) return null;
      tokenRef.current = tokens.accessToken;
      sessionTokenRef.current = tokens.sessionToken;
      writeContentCookie(tokens.accessToken, tokens.sessionToken);
      return tokens.accessToken;
    };

    return () => {
      delete window.olGetToken;
      delete window.olGetSessionToken;
      delete window.olRefreshToken;
    };
  }, [isOpen]);

  const handleIframeLoad = () => {
    // We now primarily rely on SCORM_READY, but we can keep a small delay as fallback
    // if we haven't received SCORM_READY yet.
    // However, the handshake is much safer for non-Chrome browsers.
  };

  //TODO manca il tracciamento alla chiusura dello SCORM su blocco orario
  //_______Logica blocco orario SCORM__________START
  const timeUpdateHandlerScorm = () => {
    if(canAccessNow && canAccessUntil){
      const timeLeft = differenceInSeconds(new Date(canAccessUntil), new Date());
      if(timeLeft <= 0){
        close && close();
        setTriggerPause && setTriggerPause(true);
      }
    }
  };
  //timeUpdateHandlerScorm viene eseguita ogni 250 millisecondi e se il range orarario in cui l'utente può accedere allo
  //scorm è > di new Date verrà mostrato il pop up <LearningActivityBlockOverlay>

  useEffect(() => {

    if (!isOpen || learningObjectTypology !== "DA_SCORM"|| !canAccessNow || !canAccessUntil) return;
    const intervalId = setInterval(() => {
      timeUpdateHandlerScorm();
    }, 250);
    return () => clearInterval(intervalId);
  }, [isOpen,learningObjectTypology,canAccessNow,canAccessUntil]);
  //_______Logica blocco orario SCORM__________END


  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const toRestore: HTMLElement[] = [];
    let node: HTMLElement | null = modalRef.current;

    while (node && node !== document.body) {
      const parent: HTMLElement | null = node.parentElement;
      if (parent) {
        Array.from(parent.children).forEach((sibling) => {
          const el = sibling as HTMLElement;
          if (el !== node && !el.hasAttribute("inert")) {
            el.setAttribute("inert", "");
            toRestore.push(el);
          }
        });
      }
      node = parent;
    }

    return () => {
      toRestore.forEach(el => el.removeAttribute("inert"));
    };
  }, [isOpen]);

  return (
    <InlineModal $open={ isOpen } ref={ modalRef }>
      <StyledIframe
        ref={ iframeRef }
        src={ `${uri}?${params}${shouldUsePostMessageBootstrap ? "&hasPostMessageData=true" : ""}` }
        title={ `iframe-scorm-${learningObjectTypology}` }
        onLoad={ handleIframeLoad }
      />
    </InlineModal>
  );
}
